// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import clsx from 'clsx';
import InternalBox from '../box/internal';
import InternalStatusIndicator, { InternalStatusIcon } from '../status-indicator/internal';
import styles from './styles.css.js';
const statusToColor = {
    error: 'text-status-error',
    warning: 'text-status-warning',
    success: 'text-status-success',
    info: 'text-status-info',
    stopped: 'text-status-inactive',
    pending: 'text-status-inactive',
    'in-progress': 'text-status-inactive',
    loading: 'text-status-inactive',
    'not-started': 'text-status-inactive',
    log: 'text-status-inactive',
};
const StepAnnotation = ({ children }) => {
    if (children === undefined || children === null) {
        return null;
    }
    return React.createElement("div", { className: styles.annotation }, children);
};
const StepDetails = ({ children }) => {
    if (children === undefined || children === null) {
        return null;
    }
    return React.createElement("div", { className: styles.details }, children);
};
const CustomStep = ({ step, orientation, renderStep, hideConnectors, }) => {
    const { status, statusIconAriaLabel, annotation } = step;
    const { header, details, icon } = renderStep(step);
    const iconNode = icon ? icon : React.createElement(InternalStatusIcon, { type: status, iconAriaLabel: statusIconAriaLabel });
    const connectorClassName = clsx(styles.connector, hideConnectors && styles['connector-hidden']);
    if (orientation === 'horizontal') {
        return (React.createElement("li", { className: styles.container },
            React.createElement(StepAnnotation, null, annotation),
            React.createElement("div", { className: styles['step-layout'] },
                iconNode,
                React.createElement("hr", { className: connectorClassName, role: "none" })),
            React.createElement("div", { className: styles.content },
                React.createElement("div", { className: styles.header }, header),
                React.createElement(StepDetails, null, details))));
    }
    // Vertical orientation: render the icon and the connector together in a column-1 "rail" so the
    // connector starts directly beneath the icon and stretches the full height of the step. Unlike
    // placing the header in the same row as the icon, this keeps the vertical line continuous even
    // when the custom header wraps onto multiple lines. `annotation` (for example, a timeline timestamp)
    // is rendered before the rail.
    return (React.createElement("li", { className: clsx(styles.container, styles['custom-vertical']) },
        React.createElement(StepAnnotation, null, annotation),
        React.createElement("div", { className: styles.rail },
            iconNode,
            React.createElement("hr", { className: connectorClassName, role: "none" })),
        React.createElement("div", { className: styles.content },
            React.createElement("div", { className: styles.header }, header),
            React.createElement(StepDetails, null, details))));
};
const InternalStep = ({ status, statusIconAriaLabel, header, details, annotation, orientation, hideConnectors, }) => {
    const connectorClassName = clsx(styles.connector, hideConnectors && styles['connector-hidden']);
    return (React.createElement("li", { className: styles.container },
        React.createElement(StepAnnotation, null, annotation),
        React.createElement("div", { className: styles['step-layout'] }, orientation === 'vertical' ? (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.header },
                React.createElement(InternalStatusIndicator, { __embedded: true, type: status, iconAriaLabel: statusIconAriaLabel }, header)),
            React.createElement(StepDetails, null, details),
            React.createElement("hr", { className: clsx(connectorClassName, styles['connector-continuation']), role: "none" }))) : (React.createElement(React.Fragment, null,
            React.createElement(InternalBox, { color: statusToColor[status] },
                React.createElement(InternalStatusIcon, { type: status, iconAriaLabel: statusIconAriaLabel })),
            React.createElement("hr", { className: connectorClassName, role: "none" })))),
        orientation === 'horizontal' && (React.createElement("div", { className: styles.content },
            React.createElement("div", { className: styles.header },
                React.createElement(InternalBox, { color: statusToColor[status] }, header)),
            React.createElement(StepDetails, null, details)))));
};
const InternalSteps = ({ steps, orientation, connectorLines, renderStep, ariaLabel, ariaLabelledby, ariaDescribedby, __internalRootRef, ...props }) => {
    const hideConnectors = connectorLines === 'none';
    const hasAnnotations = steps.some(step => step.annotation !== undefined && step.annotation !== null);
    return (React.createElement("div", { ...props, className: clsx(styles.root, props.className, orientation === 'horizontal' ? styles.horizontal : styles.vertical), ref: __internalRootRef },
        React.createElement("ol", { className: clsx(styles.list, orientation === 'vertical' && hasAnnotations && styles['with-annotation']), "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, "aria-describedby": ariaDescribedby }, steps.map((step, index) => renderStep ? (React.createElement(CustomStep, { key: index, orientation: orientation, step: step, renderStep: renderStep, hideConnectors: hideConnectors })) : (React.createElement(InternalStep, { key: index, status: step.status, statusIconAriaLabel: step.statusIconAriaLabel, header: step.header, details: step.details, annotation: step.annotation, orientation: orientation, hideConnectors: hideConnectors }))))));
};
export default InternalSteps;
//# sourceMappingURL=internal.js.map